<template>
    <div>
<!-- DIALOGS -->
        <SelectionRequiredDialog />
        <PeriodRequiredDialog />
        <DeletionDialog context='SelectedLabelStatementsArchive' />	
        <LabelStatementsArchivingDialog />
        <LabelStatementsLicensorPaymentsDialog />
        <StatementPreviewDialog context='LabelStatements' dir="label/statements"/>
        <BaseListPage
            headline="lang_LABEL_STATEMENTS"
            :context="context"
            :alvqa="alvqa"
            :stickyHeader="true"
        >
        <!-- FILTER -->
            <template v-slot:filter>
                <v-col cols="2">
                    <DropdownFilter
                        :context="context + 'ArchiveFilter'"
                        label="lang_GENERIC_CAPTION_FILTER"
                        :items="archiveFilterItems"
                        :defaultValueNull="true"
                        :alvqa="alvqa + '-list-dropdown-archive-filter'"
                    />
                </v-col>
            </template>
        <!-- PERIOD -->
            <template v-slot:period>
                <v-col cols="6" class="pa-0">
                    <BasePeriod 
                        class="pt-3 pl-3"
                        :context="context" 
                        widthYearField="2"
                        widthNumberField="1"
                        periodLabel="lang_GENERIC_CAPTION_PERIOD"
                    />
                </v-col>
            </template>

            <template v-slot:dataTable>
<!-- DATA TABLE -->                        
                <BaseDataTableList 
                    :headers="headers"
                    :context="context"
                    readOnlyGhostTdSpan="2"
                    :alvqa="alvqa"
                    :differentItemsPerPage="400"
                    :hasServerSideSorting='true'
                    :stickyTopSlot="true"
                >
                    <template v-slot:topSlot><LabelStatementsTopSlot /></template>
                    <!-- DATA TABLE CONTENT: columns -->
					<template v-slot:body="{ item }">
                        <ExpandIcon 
                            :isExpanded="item.isExpanded"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-icon-expand'"
                            :color="item.item.archiveRowsExist ? 'buttonStandard' : 'standardBrownIcon'"
                        />
                        <td class="no_ellipsis">
                            <BaseTooltip>
                                <template v-slot:text>{{ item.item.alvId !== null ?  $alvPlugin.getArtistOrPerformerName(item.item.licensor) + ' (' + item.item.alvId + ')' : '' }}</template>
                                <template v-slot:content>
                                    <div :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-interested-party'">
                                    {{  $alvPlugin.getArtistOrPerformerName(item.item.licensor) }} 
                                    </div>
                                </template>
                            </BaseTooltip>
                        </td>
                        <td 
                            class="text-right"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-period'"
                        >{{ $alvPlugin.displayPeriod(item.item.period) }}</td>
                         <td
                            class="text-right"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-expected-sum'"
                         >{{ $alvPlugin.getNumberLocalization(item.item.expectedSum, 'number') }}</td>
                         <td
                            class="text-right"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-payout-net'"
                         >{{ $alvPlugin.getNumberLocalization(item.item.payoutNet, 'number') }}</td>
                        <td
                            class="text-right"
                        >{{ item.item.createdOn !== null ? $d(new Date(item.item.createdOn), 'alvDateTime',  $i18n.locale) : '' }}</td>
                        <!-- <td> -->
                            <!-- GHOST COL -->
                        <!-- </td> -->
					</template>
                    <!-- EXPAND -->
                    <template v-slot:expandSlot="{ id }"><LabelStatementsReadonly :itemId="id"/></template>
                </BaseDataTableList>
            </template>
        </BaseListPage>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BaseListPage from '@/components/_base/BaseListPage'
import BaseDataTableList from '@/components/_base/BaseDataTableList'
import BasePeriod from '@/components/_base/BasePeriod'
import BaseTooltip from '@/components/_base/BaseTooltip'
import DropdownFilter from '@/components/_filters/DropdownFilter'
import ExpandIcon from '@/components/_buttons/ExpandIcon'

import LabelStatementsTopSlot from './LabelStatementsTopSlot'
import LabelStatementsReadonly from './_readonly/LabelStatementsReadonly'

import SelectionRequiredDialog from '@/components/_dialogs/SelectionRequiredDialog'
import PeriodRequiredDialog from '@/components/_dialogs/PeriodRequiredDialog'
import DeletionDialog from '@/components/_dialogs/DeletionDialog'
import LabelStatementsArchivingDialog from './_dialogs/LabelStatementsArchivingDialog'
import StatementPreviewDialog from '@/components/_dialogs/StatementPreviewDialog'
import LabelStatementsLicensorPaymentsDialog from './_dialogs/LabelStatementsLicensorPaymentsDialog.vue'

export default {
    name: "LabelStatementsList",
    components: {
        BaseListPage,
        BaseDataTableList,
        BasePeriod,
        DropdownFilter,
        ExpandIcon,
        BaseTooltip,

        LabelStatementsTopSlot,
        LabelStatementsReadonly,
        
        SelectionRequiredDialog,
        PeriodRequiredDialog,        
        DeletionDialog, 
        LabelStatementsArchivingDialog,
        StatementPreviewDialog,
        
        LabelStatementsLicensorPaymentsDialog
    },
    data() {
        return {
            context: "LabelStatements",
            alvqa: "label-statements"
        }
    },
    computed: {
        ...mapGetters(['getLabelStatementsSearch', 'getSelectedPeriodYearLabelStatements']),

        isPeriodSet() {
            const y = this.getSelectedPeriodYearLabelStatements
            return y !== null && y !== undefined && y !== ''
        },

        archiveFilterItems() {
            const periodRequired = !this.isPeriodSet
            const suffix = periodRequired ? this.$t('lang_GENERIC_CAPTION_PERIOD_REQUIRED_SUFFIX') : ''
            return [
                {
                    text: this.$t('lang_GENERIC_CAPTION_WITH_CARRY_FORWARD') + suffix,
                    value: 'archivedWithCarryForward',
                    disabled: periodRequired,
                    alvqa: this.alvqa + '-list-dropdown-archive-filter-with-carry-forward'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_WITHOUT_CARRY_FORWARD') + suffix,
                    value: 'archivedWithoutCarryForward',
                    disabled: periodRequired,
                    alvqa: this.alvqa + '-list-dropdown-archive-filter-without-carry-forward'
                }
            ]
        },

		/** Returns an Array with header-objects with props.
        *  @return {[{text: String, align: string, sortable: string, value: string}]} props of the header */        
       headers() {
           return [
				{
					text: '', 
					value: 'data-table-select', 
					align: 'center',
                    sortable: false,
					width: "60px"
				},
				{
					text: '', 
					value: 'data-table-expand',
					align: 'center', 
                    sortable: false,
					width: "48px",
				},
                {
                    text: this.$t('lang_GENERIC_CAPTION_LICENSOR'), 
                    value: 'licensor', 
                    align: 'left', 
                    sortable: true,
                    // width: "338px",
                    // class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_PERIOD'), 
                    value: 'period',
                    align: 'right', 
                    sortable: false,
                    // width: "150px",
                    // class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_RESULT_NET') + ' ' + this.$store.getters.getSelectedCurrency, 
                    value: 'expectedSum',
                    align: 'end', 
                    sortable: true,
                    width: "180px",
                    // class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_SUMMARY_PAYOUT_NET') + ' ' + this.$store.getters.getSelectedCurrency, 
                    value: 'payoutNet',
                    align: 'end', 
                    sortable: true,
                    width: "180px",
                    // class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_LABEL_STATEMENTS_CAPTION_CREATED_ON'), 
                    value: 'createdOn',
                    align: 'end', 
                    sortable: true,
                    // width: "390px",
                    // class: 'text-no-wrap'
                },
                {
					text: '', 
					value: 'ghost',
					sortable: false,
                    class: 'text-no-wrap'
				},
                {
                    text: '', 
                    value: 'downloadLink',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    // class: 'text-no-wrap'
                }
           ]
       }
    }
}
</script>